import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import { getCurrentOrganizationId } from '../../../common/selectors/auth';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';

import { HeaderCell, Table, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import Select from 'react-select';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { Icon, Pagination } from 'semantic-ui-react';
import LoadingSpinner from 'common/components/LoadingSpinner';

import AnalyticsFeedbackSlider from './AnalyticsFeedbackSlider';
// @ts-ignore
import SentimentAnalysis from 'sentiment';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { calculateRisk } from '../utils/AnalyticsUtils';

import { AggregateType } from 'common/constants/AggregateType';

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const AnalyticsFeedback: React.FC = () => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);

  const { data: feedback_data, isLoading: feedbackIsLoading, isFetching: feedbackIsFetching } =
    useApiPortalV1AssessmentsResultsListQuery({
      aggregateType: AggregateType.MEMBER_FEEDBACK,
      assessmentPeriodId: assessmentPeriodId,
    });

  const { data: data_by_option, isLoading: optionIsLoading, isFetching: optionIsFetching } =
    useApiPortalV1AssessmentsResultsListQuery({
      aggregateType: AggregateType.CATEGORY_PILLARS,
      assessmentPeriodId: assessmentPeriodId,
    });

  const isLoading = feedbackIsLoading || feedbackIsFetching || optionIsLoading || optionIsFetching;

  var sentiment = new SentimentAnalysis();

  type PillarOption = { value: number; label: string };
  const ALL_PILLARS = -1;

  const [filterPillars, setFilterPillars] = useState<PillarOption[]>([]);
  const [selectedPillar, setSelectedPillar] = useState<number | null>(null);
  const [selectedPillarName, setSelectedPillarName] = useState<string>('All Pillars');
  const [searchText, setSearchText] = useState<string>('');

  const [feedbackData, setFeedbackData] = useState([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'category_name',
    direction: 'asc',
  });

  const rowsPerPage = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [primaryCategory, setPrimaryCategory] = useState('');

  const matchesWordPrefix = (value: string, query: string): boolean => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return true;
    }

    const isPhraseQuery = normalizedQuery.split(/\s+/).length > 1;
    if (isPhraseQuery) {
      const normalizedValue = value.toLowerCase().replace(/\s+/g, ' ').trim();
      const normalizedPhrase = normalizedQuery.replace(/\s+/g, ' ');
      return normalizedValue.includes(normalizedPhrase);
    }

    return value
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .some(word => word.startsWith(normalizedQuery));
  };

  const matchesSearch = (item: any, query: string): boolean => {
    const fields = [
      item?.feedback,
      item?.prompt_feedback,
    ]
      .filter(Boolean)
      .map(value => String(value));

    return fields.some(field => matchesWordPrefix(field, query));
  };

  const sortedData = [...feedbackData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentPageData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  interface FeedbackBlockProps {
    feedback: string;
    question: string;
    prompt_question: string;
    prompt_feedback: string;
    searchQuery: string;
  }

  const renderHighlightedText = (text: string, query: string): React.ReactNode => {
    const source = String(text ?? '');
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      return source;
    }

    const isPhraseQuery = normalizedQuery.split(/\s+/).length > 1;
    const pattern = isPhraseQuery
      ? `(${escapeRegExp(normalizedQuery)})`
      : `(\\b${escapeRegExp(normalizedQuery)})`;
    const regex = new RegExp(pattern, 'ig');

    const segments = source.split(regex);
    return segments.map((segment, index) =>
      index % 2 === 1 ? <HighlightMark key={`${segment}-${index}`}>{segment}</HighlightMark> : segment
    );
  };

  const FeedbackBlock: React.FC<FeedbackBlockProps> = ({
    feedback,
    question,
    prompt_question,
    prompt_feedback,
    searchQuery,
  }) => (
    <>
      <Question>{question}</Question>
      <Text>{renderHighlightedText(feedback, searchQuery)}</Text>
      {prompt_question && String(prompt_question).trim().length > 0 && (
        <>
          <Question style={{ marginTop: 15 }}>{prompt_question}</Question>
          <Text>
            {prompt_feedback && String(prompt_feedback).trim().length > 0
              ? renderHighlightedText(prompt_feedback, searchQuery)
              : '[team member chose not to respond]'}
          </Text>
        </>
      )}
    </>
  );

  const organizationId = useSelector(getCurrentOrganizationId);
  const { setTopBarContent } = useContext(TopBarContext);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);

  useEffect(() => {
    const pillarLabel = selectedPillarName || 'All Pillars';

    setPagePrompts([
      {
        title: 'Feedback Themes',
        displayText: `Summarize the main feedback themes and sentiment risks for ${pillarLabel}.`,
        prompt:
          `Analyze the Member Feedback page for ${pillarLabel} and summarize the main themes, sentiment risks, and what the feedback reveals about employee experience. Highlight the most important patterns related to sentiment, pillar performance, and open-ended comments.`,
        description: `Use this prompt to surface the top feedback themes and risk signals for ${pillarLabel}.`,
      },
      {
        title: 'Feedback Action Plan',
        displayText: `Recommend how leaders should respond to feedback for ${pillarLabel}.`,
        prompt:
          `Based on the ${pillarLabel} feedback and sentiment analysis, recommend how leadership should respond. Focus on targeted actions that address the biggest concerns while reinforcing positive feedback.`,
        description: `Ask Copilot for a feedback-driven action plan focused on ${pillarLabel}.`,
      },
      {
        title: 'Risk and Opportunity',
        displayText: `What are the top risks and opportunities in feedback for ${pillarLabel}?`,
        prompt:
          `Identify the top risks and opportunities present in the feedback data for ${pillarLabel}. Include which pillars and groups should be prioritized to reduce risk and which strengths can be scaled.`,
        description: `Use this prompt to identify risk and opportunity from open-ended feedback for ${pillarLabel}.`,
      },
    ]);

    setPageInfoTitle('About Member Feedback');
    setPageInfoIntro(
      'This page analyzes open-ended employee feedback through sentiment and pillar-level response patterns. It helps leaders understand what people are saying, which areas feel risky, and where improvements should be made.',
    );
    setPageInfoItems([
      {
        term: 'Sentiment themes',
        description: 'Capture the most important positive and negative themes in the feedback.',
      },
      {
        term: 'Risk signals',
        description: 'Use sentiment strength and word choice to understand where the organization may need to act quickly.',
      },
      {
        term: 'Response actions',
        description: 'Turn qualitative feedback into concrete leadership actions.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro, selectedPillarName]);

  useEffect(() => {
    if (data_by_option && data_by_option.length > 0) {
      const pillars: PillarOption[] = Array.from(
        new Map<string, PillarOption>(
          data_by_option[0]?.result.pillars.map(
            (score: { pillar_name: string; pillar: number }) => [
              score.pillar,
              { value: score.pillar, label: score.pillar_name },
            ]
          )
        ).values()
      );
      const allPillars = { value: ALL_PILLARS, label: 'All Pillars' };
      const updatedPillars = [allPillars, ...pillars];

      setFilterPillars(updatedPillars);
      const selectedPillarExists = updatedPillars.some(pillar => pillar.value === selectedPillar);
      const initialPillar = selectedPillar === null || !selectedPillarExists ? updatedPillars[0] : updatedPillars.find(p => p.value === selectedPillar);
      if (initialPillar) {
        setSelectedPillar(initialPillar.value);
        setSelectedPillarName(initialPillar.label);
      }
    }
  }, [data_by_option, selectedPillar]);

  useEffect(() => {
    if (feedback_data) {
      const sourceRows = feedback_data[0]?.result || [];
      const pillarFilteredRows = selectedPillar === ALL_PILLARS
        ? sourceRows
        : sourceRows.filter((factor: { factor: number }) => factor.factor === selectedPillar);

      const searchFilteredRows = pillarFilteredRows.filter((item: any) =>
        matchesSearch(item, searchText)
      );

      const updatedFeedbackData = searchFilteredRows.map((item: any) => ({
        ...item,
        risk: calculateRiskLocal(item.response, item.feedback + item.prompt_feedback),
      }));

      setFeedbackData(updatedFeedbackData);
      setCurrentPage(1);

      const primaryCategoryName =
        feedback_data[0]?.result?.[0]?.primary_category || 'No primary category found';
      setPrimaryCategory(primaryCategoryName);
    }
  }, [feedback_data, selectedPillar, searchText]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handlePageChange = (_: React.MouseEvent<HTMLAnchorElement>, data: any) => {
    setCurrentPage(data.activePage as number);
  };

  const SortUp = (): JSX.Element => (
    <Icon name="caret up" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id={'label'} />
  );
  const SortDown = (): JSX.Element => (
    <Icon name="caret down" style={{ margin: 0, marginTop: -8, zIndex: 10 }} id={'label'} />
  );

  // Using calculateRisk from AnalyticsUtils
  const calculateRiskLocal = (score: number, feedback: string) => calculateRisk(score, feedback, sentiment);

  return (
    <PageContent>
      <PageSection>
        <PageHeader
          title="Member Feedback"
          caption="Sentiment analysis of open-ended feedback by pillar and primary demographic."
          controls={
            <>
              <SearchInput
                type="text"
                placeholder="Search feedback"
                value={searchText}
                onChange={event => setSearchText(event.target.value)}
                aria-label="Search feedback"
              />
              <Select
                options={filterPillars}
                placeholder="Select Pillar"
                value={filterPillars.find(option => option.value === selectedPillar)}
                onChange={option => {
                  setSelectedPillar(option ? option.value : null);
                  setSelectedPillarName(option ? option.label : 'All Pillars');
                }}
                menuPortalTarget={document.body}
                styles={{
                  container: base => ({
                    ...base,
                    width: '250px',
                  }),
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                }}
              />
            </>
          }
        />
        {isLoading ? (
          <LoadingWrapper>
            <LoadingSpinner />
          </LoadingWrapper>
        ) : (
          <Table>
            <TableHeader>
              <HeaderCell
                width={2}
                selectable
                onClick={() => handleSort('factor_name')}
                verticalAlign="top"
              >
                Pillar{' '}
                {sortConfig.key === 'factor_name' &&
                  (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
              </HeaderCell>
              <HeaderCell width={2} selectable onClick={() => handleSort('primary_option')}>
                {primaryCategory}{' '}
                {sortConfig.key === 'primary_option' &&
                  (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
              </HeaderCell>
              <HeaderCell width={8} selectable onClick={() => handleSort('question')}>
                Feedback{' '}
                {sortConfig.key === 'question' &&
                  (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
              </HeaderCell>
              <HeaderCell width={4} selectable onClick={() => handleSort('risk')}>
                Sentiment{' '}
                {sortConfig.key === 'risk' && (sortConfig.direction === 'asc' ? SortUp() : SortDown())}
              </HeaderCell>
            </TableHeader>
            {currentPageData.length === 0 ? (
              <TableRow>
                <TableCell width={16} colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#7e7e7e' }}>
                  <div style={{ width: '100%', textAlign: 'center' }}>No feedback available for this pillar.</div>
                </TableCell>
              </TableRow>
            ) : currentPageData.map(
              (
                row: {
                  primary_option: string;
                  feedback: string;
                  question: string;
                  prompt_question: string;
                  prompt_feedback: string;
                  factor_name: string;
                  response: number;
                  risk: number;
                },
                index: React.Key
              ) => (
                <TableRow key={index}>
                  <TableCell width={2}>
                    <InsightsText>{row.factor_name}</InsightsText>
                  </TableCell>
                  <TableCell width={2} $firstcol>
                    <InsightsText>{row.primary_option}</InsightsText>
                  </TableCell>
                  <TableCell width={8}>
                    <FeedbackBlock
                      feedback={row.feedback}
                      question={row.question}
                      prompt_question={row.prompt_question}
                      prompt_feedback={row.prompt_feedback}
                      searchQuery={searchText}
                    />
                  </TableCell>
                  <TableCell width={4}>
                    <AnalyticsFeedbackSlider risk={row.risk} />
                  </TableCell>
                </TableRow>
              )
            )}
          </Table>
        )}

        <PageSection display="flex" justifyContent="flex-end">
          <Pagination
            activePage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            boundaryRange={1} // Number of pages to display on each end
            siblingRange={1} // Number of pages to display around the current page
            ellipsisItem={{ content: '...', icon: true }} // Ellipsis for overflow
            firstItem={null}
            lastItem={null}
          />
        </PageSection>
      </PageSection>
    </PageContent>
  );
};

export default AnalyticsFeedback;

const InsightsText = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Question = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  /* identical to box height */

  color: #414141;
  margin-bottom: 5px;
`;

const Text = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 15px;

  color: #7e7e7e;
`;

const HighlightMark = styled.mark`
  background: #fde68a;
  color: #111827;
  padding: 0 1px;
  border-radius: 2px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
`;

const SearchInput = styled.input`
  width: 250px;
  height: 38px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 14px;
`;

